import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { fetchProjects } from '../store/slices/projectSlice'
import { PlusIcon, FolderIcon, UsersIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import CreateProjectModal from '../components/CreateProjectModal'
import { usePermissions, Permission } from '../hooks/usePermissions'
import { getImageUrl } from '../utils/formatters'

const Dashboard = () => {
  const dispatch = useAppDispatch()
  const { projects, loading } = useAppSelector(state => state.project)
  const { user } = useAppSelector(state => state.auth)
  const { hasPermission } = usePermissions()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [failedIcons, setFailedIcons] = useState<Set<string>>(new Set())
  
  // 檢查使用者是否有建立專案權限
  const canCreateProject = hasPermission(Permission.CREATE_PROJECT)

  useEffect(() => {
    // 只有在用戶完全認證後才獲取項目
    if (user && user.id) {
      console.log('Dashboard mounted, dispatching fetchProjects')
      dispatch(fetchProjects())
    }
  }, [dispatch, user])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">專案儀表板</h1>
          <p className="text-gray-600">歡迎回來，{user?.name}</p>
        </div>
        {canCreateProject && (
          <button 
            onClick={() => setIsCreateModalOpen(true)}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            建立專案
          </button>
        )}
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-12">
          <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">沒有專案</h3>
          <p className="mt-1 text-sm text-gray-500">
            開始建立您的第一個專案
          </p>
          {canCreateProject && (
            <div className="mt-6">
              <button 
                onClick={() => setIsCreateModalOpen(true)}
                className="btn-primary"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                建立專案
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <Link
              key={project.id}
              to={`/project/${project.key}/board`}
              className="card p-6 hover:shadow-lg transition-shadow duration-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-primary-100 flex items-center justify-center">
                    {project.icon && !failedIcons.has(project.id) ? (
                      <img
                        src={getImageUrl(project.icon) || project.icon}
                        alt={`${project.name} icon`}
                        className="w-full h-full object-cover"
                        onError={() => {
                          setFailedIcons(prev => new Set(prev).add(project.id))
                        }}
                      />
                    ) : (
                      <FolderIcon className="h-6 w-6 text-primary-600" />
                    )}
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      {project.name}
                    </h3>
                    <p className="text-sm text-gray-500">{project.key}</p>
                  </div>
                </div>
              </div>

              {project.description && (
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {project.description}
                </p>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center text-sm text-gray-500">
                  <UsersIcon className="h-4 w-4 mr-1" />
                  {project.members?.length || 0} 成員
                </div>
                <div className="flex -space-x-2">
                  {project.members?.slice(0, 3).map((member) => (
                    <div key={member.id} className="relative">
                      {member.user.avatar ? (
                        <img
                          src={member.user.avatar}
                          alt={member.user.name}
                          className="w-8 h-8 rounded-full border-2 border-white"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-600">
                            {member.user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                  {(project.members?.length || 0) > 3 && (
                    <div className="w-8 h-8 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center">
                      <span className="text-xs font-medium text-gray-600">
                        +{(project.members?.length || 0) - 3}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </div>
  )
}

export default Dashboard